import { HTTP_CONFLICT } from '../arch/constants'
import { getQueryParamInsensitive, getSignupTrackingFields, handleErrorWithStateHooks } from '../common'
import { clearHeldIepReviewEvents } from '../tracking/segment'

const IEP_REVIEW_JOIN_ROUTE = 'join/iep-review'
const IEP_REVIEW_EXPIRED_ROUTE = 'confirmations/iep-review/expired'
const IEP_REVIEW_VIEWER_ROUTE = 'iep-review'
const IEP_REVIEW_LINK_ROUTE = 'confirmations/iep-review/:code'

const IEP_REVIEW_JOIN_PATH = `/${IEP_REVIEW_JOIN_ROUTE}`
const IEP_REVIEW_EXPIRED_PATH = `/${IEP_REVIEW_EXPIRED_ROUTE}`

const IEP_REVIEW_OUTCOME_ANALYSIS_COMPLETE = 'analysisComplete'
const IEP_REVIEW_OUTCOME_FAILED = 'failed'
const IEP_REVIEW_OUTCOME_NOT_AN_IEP = 'notAnIep'

// The parent’s place in the flow, as saved by writeIepReviewState.
const STEP_UPLOAD = 'upload'
const STEP_ACCOUNT = 'account'
const STEP_CONFIRM = 'confirm'
const STEP_ANALYZING = 'analyzing'
const STEP_RETRY = 'retry'
const STEP_READY = 'ready'

const IEP_REVIEW_STATE_KEY = 'iepReviewSignup'

const PREVIEW_DATABASE_NAME = 'iepReview'
const PREVIEW_STORE_NAME = 'preview'
const PREVIEW_RECORD_KEY = 'file'
const PREVIEW_LIFETIME_MS = 12 * 60 * 60 * 1000

const ANALYSIS_IN_PROGRESS_ERROR = 'Your IEP is already being reviewed. Check back in a few minutes.'
const NETWORK_ERROR = 'We couldn’t reach Undivided. Check your connection and try again.'
const GENERIC_ERROR = 'Something went wrong. Please try again.'

/**
 * Convenience function for building the path to the IEP review viewer for the given child and IEP.
 */
const iepReviewViewerPath = (clientId, iepId) => `/${IEP_REVIEW_VIEWER_ROUTE}/${clientId}/${iepId}`

/**
 * Determines whether the parent still needs to set a password for their IEP review account.
 */
const isIepReviewPasswordPending = appUser => appUser?.iepReviewStatus?.passwordPending === true

/**
 * Determines whether the server’s IEP review status still refers to one of the IEPs from before this
 * upload. Right after a claim or retry, the server keeps reporting the previous IEP until the background
 * job points it at the new one, so we can’t take a stale “failed” or “ready” as this upload’s result.
 */
const isStatusForPreviousIep = (iepReviewStatus, previousIepIds = []) =>
  Boolean(iepReviewStatus?.documentId) && previousIepIds.includes(iepReviewStatus.documentId)

/**
 * Convenience function for getting the IEP ID out of the server’s IEP review status. Returns null if
 * there isn’t one or if it belongs to one of the previous IEPs.
 */
const getIepIdFromStatus = (iepReviewStatus, previousIepIds) =>
  isStatusForPreviousIep(iepReviewStatus, previousIepIds) ? null : (iepReviewStatus?.documentId ?? null)

/**
 * Interprets the server’s IEP review status for the current upload, returning { readyIepId } when the
 * analysis is ready, { failed, notAnIep, retry } when it failed or the upload wasn’t an IEP, or null while it is
 * still working. A status that is about a previous IEP counts as still working.
 */
const getIepReviewOutcome = (iepReviewStatus, previousIepIds) => {
  if (isStatusForPreviousIep(iepReviewStatus, previousIepIds)) {
    return null
  }

  const { documentId, childId, outcome } = iepReviewStatus ?? {}

  if (documentId && outcome === IEP_REVIEW_OUTCOME_ANALYSIS_COMPLETE) {
    return { readyIepId: documentId }
  }

  if (outcome === IEP_REVIEW_OUTCOME_FAILED || outcome === IEP_REVIEW_OUTCOME_NOT_AN_IEP) {
    return {
      failed: true,
      notAnIep: outcome === IEP_REVIEW_OUTCOME_NOT_AN_IEP,
      retry: childId && documentId ? { clientId: childId, failedIepId: documentId } : null
    }
  }

  return null
}

/**
 * Determines whether the server decided the given IEP review upload wasn’t an IEP.
 */
const isNotAnIep = (iepReviewStatus, iepId) =>
  iepReviewStatus?.outcome === IEP_REVIEW_OUTCOME_NOT_AN_IEP && iepReviewStatus.documentId === iepId

/**
 * Retrieves the parent’s saved place in the flow (their step and uploads) from sessionStorage.
 */
const readIepReviewState = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(IEP_REVIEW_STATE_KEY))
  } catch (error) {
    return null
  }
}

/**
 * Saves the parent’s place in the flow to sessionStorage.
 */
const writeIepReviewState = state => {
  try {
    window.sessionStorage.setItem(IEP_REVIEW_STATE_KEY, JSON.stringify(state))
  } catch (error) {
    // No-op—storage can be blocked by browser settings, in which case the flow just won’t survive a refresh.
  }
}

// An IEP review signup has only an email, so the server fills the names in: the nickname with the email’s local
// part and the family name with a dash. Finishing the account replaces both.
const PLACEHOLDER_FAMILY_NAME = '-'

/**
 * Determines whether the user’s nickname is still the placeholder the IEP review signup gave it.
 */
const isPlaceholderNickname = appUser => Boolean(appUser?.nickname) && appUser.nickname === appUser.email?.split('@')[0]

/**
 * Determines whether the user’s family name is still the placeholder the IEP review signup gave it.
 */
const isPlaceholderFamilyName = appUser => appUser?.familyName === PLACEHOLDER_FAMILY_NAME

/**
 * Returns the signup fields that carry this tab’s IEP review upload through a Google sign-in, if the upload is
 * awaiting an account and the sign-in is on its way back to the IEP review; otherwise undefined, so that a Google
 * sign-in from anywhere else doesn’t start an analysis the parent didn’t ask for.
 *
 * @param {string} from The path the sign-in returns to
 */
const getPendingIepReviewSignup = from => {
  const { step, stagingKeys } = readIepReviewState() ?? {}
  const awaitingAccount = step === STEP_ACCOUNT && stagingKeys?.length > 0

  return awaitingAccount && from?.startsWith(IEP_REVIEW_JOIN_PATH) ? { stagingKeys } : undefined
}

/**
 * Removes the parent’s saved place in the flow, along with any of its Segment events that are still being
 * held until the parent is identified.
 */
const clearIepReviewState = () => {
  try {
    window.sessionStorage.removeItem(IEP_REVIEW_STATE_KEY)
  } catch (error) {
    // No-op
  }

  clearHeldIepReviewEvents()
  clearIepReviewPreview()
}

const openPreviewDatabase = () =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(PREVIEW_DATABASE_NAME, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(PREVIEW_STORE_NAME)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

const runPreviewRequest = async (mode, makeRequest) => {
  const database = await openPreviewDatabase()
  try {
    return await new Promise((resolve, reject) => {
      const request = makeRequest(database.transaction(PREVIEW_STORE_NAME, mode).objectStore(PREVIEW_STORE_NAME))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  } finally {
    database.close()
  }
}

/**
 * Keeps the first uploaded file in IndexedDB so its preview survives a reload, tagged with its staging key so it
 * is only shown for that upload. Failures are ignored: without the saved file, the card just shows an icon.
 */
const saveIepReviewPreview = (stagingKey, file) =>
  runPreviewRequest('readwrite', store =>
    store.put({ stagingKey, file, savedAt: Date.now() }, PREVIEW_RECORD_KEY)
  ).catch(() => {})

const readIepReviewPreview = () => runPreviewRequest('readonly', store => store.get(PREVIEW_RECORD_KEY))

const isPreviewFresh = saved => saved.savedAt > Date.now() - PREVIEW_LIFETIME_MS

/**
 * Retrieves the file saved by saveIepReviewPreview if it belongs to the given staging key, or null. A preview
 * older than 12 hours is removed instead, so the parent’s IEP doesn’t linger in a browser they’ve walked away from.
 */
const loadIepReviewPreview = async stagingKey => {
  try {
    const saved = await readIepReviewPreview()
    if (saved && !isPreviewFresh(saved)) {
      clearIepReviewPreview()
      return null
    }

    return saved?.stagingKey === stagingKey ? saved.file : null
  } catch (error) {
    return null
  }
}

/**
 * Removes the saved preview file if it has expired or no longer belongs to this tab’s saved place in the flow.
 * loadIepReviewPreview only runs while the flow is under way, so this is what catches an IEP left behind by a tab
 * that was closed before the flow finished; it runs when the app starts.
 */
const expireIepReviewPreview = async () => {
  try {
    const saved = await readIepReviewPreview()
    if (saved && (!isPreviewFresh(saved) || saved.stagingKey !== readIepReviewState()?.stagingKeys?.[0])) {
      await clearIepReviewPreview()
    }
  } catch (error) {
    // No-op—the preview is a convenience, and loadIepReviewPreview applies the same expiry when it is read.
  }
}

/**
 * Removes the saved preview file, so the parent’s IEP doesn’t stay in the browser once it’s no longer needed.
 */
const clearIepReviewPreview = () =>
  runPreviewRequest('readwrite', store => store.delete(PREVIEW_RECORD_KEY)).catch(() => {})

/**
 * Determines what to tell the parent when a request in the IEP review flow fails, given the server’s JSON
 * error payload and the response status. Server and browser messages are written for developers, so we never
 * show them to the parent as-is. Without a payload, the request most likely never reached the server: either
 * the network failed or a gateway responded with an HTML error page. A conflict means that an analysis is
 * already under way.
 */
const getIepReviewErrorMessage = (errorPayload, status) => {
  if (!errorPayload) {
    return NETWORK_ERROR
  }

  return status === HTTP_CONFLICT ? ANALYSIS_IN_PROGRESS_ERROR : GENERIC_ERROR
}

/**
 * Convenience wrapper around `handleErrorWithStateHooks` for the IEP review flow. It sets the parent-facing
 * message from `getIepReviewErrorMessage` instead of the server’s or browser’s own.
 */
const handleIepReviewError = (error, setError, baseErrorSetters) =>
  handleErrorWithStateHooks(
    { response: error.response, message: getIepReviewErrorMessage(null) },
    setError,
    baseErrorSetters,
    { responseMessage: (response, errorPayload) => getIepReviewErrorMessage(errorPayload, response.status) }
  )

/**
 * Convenience wrapper around `getHookPromiseErrorHandler` for the IEP review flow (see `handleIepReviewError`).
 */
const getIepReviewErrorHandler = (setError, baseErrorSetters) => error =>
  handleIepReviewError(error, setError, baseErrorSetters)

const getSignupCampaign = (searchParams, pathname, search) =>
  searchParams.get('referring_page') ?? `${pathname}${search}`

/**
 * Helper function for building the saved state of a new upload: its staging keys and file names, plus the
 * campaign, referring object, and tracking fields that signup sends to the server.
 */
const buildIepReviewState = ({ uploads, searchParams, pathname, search }) => ({
  stagingKeys: uploads.map(upload => upload.stagingKey),
  fileNames: uploads.map(upload => upload.fileName),
  campaign: getSignupCampaign(searchParams, pathname, search),
  referringObject: getQueryParamInsensitive(searchParams, 'referringObject') ?? undefined,
  tracking: getSignupTrackingFields(searchParams)
})

export {
  STEP_UPLOAD,
  STEP_ACCOUNT,
  STEP_CONFIRM,
  STEP_ANALYZING,
  STEP_RETRY,
  STEP_READY,
  getPendingIepReviewSignup,
  isPlaceholderNickname,
  isPlaceholderFamilyName,
  IEP_REVIEW_JOIN_ROUTE,
  IEP_REVIEW_EXPIRED_ROUTE,
  IEP_REVIEW_VIEWER_ROUTE,
  IEP_REVIEW_LINK_ROUTE,
  IEP_REVIEW_JOIN_PATH,
  IEP_REVIEW_EXPIRED_PATH,
  iepReviewViewerPath,
  isIepReviewPasswordPending,
  getIepIdFromStatus,
  getIepReviewOutcome,
  isNotAnIep,
  readIepReviewState,
  writeIepReviewState,
  clearIepReviewState,
  saveIepReviewPreview,
  loadIepReviewPreview,
  expireIepReviewPreview,
  clearIepReviewPreview,
  getIepReviewErrorMessage,
  handleIepReviewError,
  getIepReviewErrorHandler,
  buildIepReviewState
}
